import {Account, AccountType} from '../../components/Search/SearchNav/types';
import {
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {getStorageSub, useAccountPrivileges} from '../accounts/pricing';
import {useRampFlagInferenceCatalog} from '../rampFeatureFlags';
import {
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
} from './utils';

export function useGetInferenceUsageDetails(selectedAccount: Account | null) {
  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {
    privileges: {inferenceLimitDateInterval, inferenceLimits: privilegeLimits},
  } = privilegeResults;
  const skip =
    selectedAccount?.name == null ||
    selectedAccount?.accountType === AccountType.Personal;
  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: skip ? '' : selectedAccount.name},
    skip,
  });

  // use storage sub as substitute for now
  const subscription = getStorageSub(
    billingPeriodResults.data?.organization ?? undefined
  );

  const enterpriseInferenceSubscription =
    billingPeriodResults.data?.organization?.inferenceEnterpriseSubscription;

  const isUsagePaid =
    subscription != null && enterpriseInferenceSubscription != null;

  const enterpriseLimitDateInterval =
    enterpriseInferenceSubscription != null
      ? {
          start: new Date(enterpriseInferenceSubscription.billingPeriodStart),
          end: new Date(enterpriseInferenceSubscription.billingPeriodEnd),
        }
      : undefined;

  const limits = {
    ...privilegeLimits,
  };
  const enterpriseFreeLimit =
    enterpriseInferenceSubscription?.inferenceLimit != null
      ? Number(BigInt(enterpriseInferenceSubscription?.inferenceLimit))
      : undefined;

  if (enterpriseFreeLimit != null) {
    limits.inferenceFreeLimit = enterpriseFreeLimit;
  }

  const finalLimitDateInterval =
    enterpriseLimitDateInterval ?? inferenceLimitDateInterval;

  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(finalLimitDateInterval)
    : getIntervalMarkersFromSubscription(subscription) ||
      getIntervalMarkersFromCurrentCalendarMonth();
  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.InferenceCost,
    usagePeriod,
    skip
  );

  const results = [billingPeriodResults, privilegeResults, usageResults];

  const loading = results.some(result => result.loading);
  const error = results.find(result => result?.error)?.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage} = usageResults;
  const enabled = useRampFlagInferenceCatalog(selectedAccount?.name ?? '');
  return {
    billingPeriodStart,
    billingPeriodEnd,
    isUsagePaid,
    enabled,
    usage,
    limits,
    loading,
    error,
  };
}
